import React from "react";

/**
 * PropertyPreview Component
 * ========================
 *
 * Displays property information in different states:
 * 1. Loading: Shows spinner while fetching property data
 * 2. Not Found: Shows error message for unsupported addresses/zip codes
 * 3. Preview: Shows property details with map images and property info
 *
 * State Priority (highest to lowest):
 * - isLoadingEnrichment: Shows loading spinner
 * - propertyNotFound: Shows "not supported" message
 * - showPreview: Shows property details
 * - Default: Renders nothing
 *
 * @param {boolean} isLoadingEnrichment - Whether property data is being fetched
 * @param {boolean} propertyNotFound - Whether the address/zip is not supported
 * @param {boolean} showPreview - Whether to show property details
 * @param {string} header - Header text for the property section
 * @param {Object} ttbData - Property data from enrichment API
 * @param {Object} panoramaContainerRef - Ref for street view container
 * @param {Object} satelliteContainerRef - Ref for satellite view container
 * @param {number} imageWidth - Width of property images
 * @param {number} imageHeight - Height of property images
 */
const PropertyPreview = React.memo(({
  isLoadingEnrichment,
  propertyNotFound,
  showPreview,
  header,
  ttbData,
  panoramaContainerRef,
  satelliteContainerRef,
  imageWidth,
  imageHeight,
}) => {
  // ============================================================================
  // LOADING STATE - Show spinner while fetching data
  // ============================================================================
  if (isLoadingEnrichment) {
    return (
      <div className="property-selection-loader">
        <div className="property-loader-inner">
          <svg
            className="animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="theme-clr-svg"
              cx="12"
              cy="12"
              r="10"
              stroke="#242c2c"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            />
          </svg>
          <span className="property-loader-txt">
            Loading property details…
          </span>
        </div>
      </div>
    );
  }

  // ============================================================================
  // NOT FOUND STATE - Address/zip not supported
  // ============================================================================
  if (propertyNotFound) {
    return (
      <div className="property-result-div mt-4 noResult">
        <div className="ps-heading heading-b">
          <p>{header || "Property"}</p>
        </div>
        <div className="p-6 text-center text-gray-700">
          <p className="font-medium">Property Address Not Supported</p>
          <p className="text-sm mt-2">
            The address you entered is incorrect or the State/County is not
            supported.
          </p>
        </div>
      </div>
    );
  }

  // ============================================================================
  // PREVIEW STATE - Show property details and images
  // ============================================================================
  if (showPreview) {
    return (
      <div className="property-result-div mt-4" >
        <div className="ps-heading heading-b">
          <p>{header}</p>
        </div>

        <div className="property-pics">
          <div className="pics-a">
            <div
              ref={panoramaContainerRef}
              className="pp-map"
              style={{ width: `${imageWidth}px`, height: `${imageHeight}px` }}
            />
          </div>

          <div className="pics-b">
            <div
              ref={satelliteContainerRef}
              className="pp-map"
              style={{
                width: `${imageWidth}px`,
                height: `${imageHeight}px`,
                backgroundColor: "#f3f4f6",
              }}
            />
          </div>
        </div>

        <div className="property-details">
          <div className="property-details-tbl">
            <table>
              <thead>
                <tr>
                  <th>APN</th>
                  <th>Owner</th>
                  <th>Type</th>
                  <th>Beds</th>
                  <th>Baths</th>
                  <th>Sqft</th>
                  <th>Year-built</th>
                  <th>County</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{ttbData?.apn || "NA"}</td>
                  <td>{ttbData?.ownername || "NA"}</td>
                  <td>{ttbData?.type || "NA"}</td>
                  <td>{ttbData?.bed || "NA"}</td>
                  <td>{ttbData?.bath || "NA"}</td>
                  <td>{ttbData?.sqft || "NA"}</td>
                  <td>{ttbData?.built || "NA"}</td>
                  <td>{ttbData?.county_name || "NA"}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return null;
});

PropertyPreview.displayName = "PropertyPreview";

export default PropertyPreview;